/**
 * Клиент Centrifugo v6 поверх WebSocket, протокол JSON.
 *
 * Умеет ровно то, что нужно кабинету: подключиться с токеном, держать соединение
 * (ping/pong), продлевать токен без разрыва, переподключаться с нарастающей
 * паузой и отдавать статус наружу. Подписки добавляются методом subscribe.
 *
 * Библиотека centrifuge-js не подключается намеренно: из всего протокола здесь
 * используются четыре кадра, а лишняя зависимость в бандле не нужна.
 */

export type RealtimeStatus = 'disconnected' | 'connecting' | 'connected';

export interface RealtimePublication {
  channel: string;
  data: unknown;
}

export interface CentrifugoClientOptions {
  /** Адрес вида wss://host/connection/websocket. */
  url: string;
  /** Токен для connect. Запрашивается заново на каждое подключение. */
  getToken: () => Promise<string | null>;
  /** Каналы, на которые подписываемся сразу после connect. */
  channels?: string[];
  onStatusChange?: (status: RealtimeStatus) => void;
  onPublication?: (publication: RealtimePublication) => void;
  /** Время последней активности сервера — для индикатора «онлайн». */
  onActivity?: (at: Date) => void;
}

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
/** Если сервер молчит дольше двух своих ping-интервалов, считаем соединение мёртвым. */
const PING_TIMEOUT_FACTOR = 2.5;
const DEFAULT_PING_INTERVAL_SECONDS = 25;
/**
 * За сколько до истечения продлевать токен.
 *
 * Меньше минуты: `ensureFreshAccessToken` выдаёт новый токен, только когда
 * старому осталось меньше 60 секунд. Попросить раньше — вернётся тот же самый,
 * сервер продлит на прежний срок, и так по кругу.
 */
const REFRESH_LEAD_MS = 45_000;
const REFRESH_MIN_DELAY_MS = 5_000;

interface ConnectReply {
  client?: string;
  ping?: number;
  pong?: boolean;
  /** Соединение живёт не дольше токена. */
  expires?: boolean;
  ttl?: number;
}

interface RefreshReply {
  expires?: boolean;
  ttl?: number;
}

interface ServerFrame {
  id?: number;
  connect?: ConnectReply;
  refresh?: RefreshReply;
  subscribe?: unknown;
  error?: { code?: number; message?: string };
  push?: { channel?: string; pub?: { data?: unknown } };
}

export class CentrifugoClient {
  private readonly options: CentrifugoClientOptions;
  private socket: WebSocket | null = null;
  private status: RealtimeStatus = 'disconnected';
  private commandId = 0;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private refreshTimer: number | null = null;
  private pingIntervalMs = DEFAULT_PING_INTERVAL_SECONDS * 1000;
  private pongExpected = true;
  private stopped = true;

  constructor(options: CentrifugoClientOptions) {
    this.options = options;
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  /** Запускает подключение и берёт на себя переподключения. */
  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.openSocket();
  }

  /** Закрывает соединение и отменяет запланированные попытки. */
  stop(): void {
    this.stopped = true;
    this.clearTimers();
    this.reconnectAttempt = 0;

    if (this.socket) {
      // Снимаем обработчики: закрытие по своей воле переподключение не запускает.
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }

    this.setStatus('disconnected');
  }

  subscribe(channel: string): void {
    this.send({ id: this.nextId(), subscribe: { channel } });
  }

  private setStatus(next: RealtimeStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.options.onStatusChange?.(next);
  }

  private nextId(): number {
    this.commandId += 1;
    return this.commandId;
  }

  private send(frame: Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(frame));
  }

  private async openSocket(): Promise<void> {
    if (this.stopped) return;

    this.setStatus('connecting');

    let token: string | null = null;
    try {
      token = await this.options.getToken();
    } catch {
      token = null;
    }

    if (this.stopped) return;

    if (!token) {
      // Без токена подключаться бессмысленно — ждём и пробуем снова.
      this.scheduleReconnect();
      return;
    }

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.options.url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;

    socket.onopen = () => {
      this.send({ id: this.nextId(), connect: { token, name: 'unit1-admin-web' } });
    };

    socket.onmessage = (event) => this.handleMessage(event.data);

    socket.onerror = () => {
      // Подробностей браузер не даёт; закрытие придёт следом в onclose.
    };

    socket.onclose = () => {
      this.socket = null;
      this.clearTimers();
      this.setStatus('disconnected');
      this.scheduleReconnect();
    };
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;

    this.options.onActivity?.(new Date());
    this.armPingTimeout();

    // Centrifugo может слать несколько кадров одним сообщением, разделяя переводом строки.
    for (const line of raw.split('\n')) {
      const text = line.trim();
      if (!text) continue;

      let frame: ServerFrame;
      try {
        frame = JSON.parse(text) as ServerFrame;
      } catch {
        continue;
      }

      // Пустой объект — ping от сервера, отвечаем таким же.
      if (Object.keys(frame).length === 0) {
        if (this.pongExpected) this.send({});
        continue;
      }

      if (frame.error) {
        console.warn('[Realtime] Ошибка от Centrifugo.', frame.error);
        continue;
      }

      if (frame.connect) {
        this.onConnected(frame.connect);
        continue;
      }

      if (frame.refresh) {
        // Токен продлили: сервер сказал, сколько теперь жить соединению.
        if (frame.refresh.expires) this.scheduleRefresh(frame.refresh.ttl);
        continue;
      }

      const push = frame.push;
      if (push?.pub && push.channel) {
        this.options.onPublication?.({ channel: push.channel, data: push.pub.data });
      }
    }
  }

  private onConnected(reply: ConnectReply): void {
    this.reconnectAttempt = 0;
    this.pongExpected = reply.pong !== false;
    this.pingIntervalMs = (reply.ping ?? DEFAULT_PING_INTERVAL_SECONDS) * 1000;
    this.setStatus('connected');
    this.armPingTimeout();

    // Токен живёт около четверти часа. Без продления Centrifugo рвала бы
    // связь по его истечении, и индикатор мигал бы каждые пятнадцать минут.
    if (reply.expires) this.scheduleRefresh(reply.ttl);

    for (const channel of this.options.channels ?? []) {
      this.subscribe(channel);
    }
  }

  private scheduleRefresh(ttlSeconds: number | undefined): void {
    if (this.refreshTimer !== null) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const ttlMs = Number(ttlSeconds) * 1000;
    if (!Number.isFinite(ttlMs) || ttlMs <= 0) return;

    const delay = Math.max(ttlMs - REFRESH_LEAD_MS, REFRESH_MIN_DELAY_MS);

    this.refreshTimer = window.setTimeout(() => {
      this.refreshTimer = null;
      void this.sendRefresh();
    }, delay);
  }

  private async sendRefresh(): Promise<void> {
    const live = this.socket;
    if (this.stopped || live?.readyState !== WebSocket.OPEN) return;

    let token: string | null = null;
    try {
      token = await this.options.getToken();
    } catch {
      token = null;
    }

    // Пока ходили за токеном, соединение могло смениться.
    if (this.stopped || this.socket !== live) return;

    if (!token) {
      // Продлевать нечем — честнее оборвать и переподключиться.
      live.close();
      return;
    }

    this.send({ id: this.nextId(), refresh: { token } });
  }

  /** Сервер обязан слать ping; молчание дольше срока означает мёртвое соединение. */
  private armPingTimeout(): void {
    if (this.pingTimer !== null) window.clearTimeout(this.pingTimer);

    this.pingTimer = window.setTimeout(() => {
      this.socket?.close();
    }, this.pingIntervalMs * PING_TIMEOUT_FACTOR);
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer !== null) return;

    const base = Math.min(
      INITIAL_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempt,
      MAX_RECONNECT_DELAY_MS,
    );
    // Разброс, чтобы вкладки не ломились одновременно после общего обрыва.
    const delay = base / 2 + Math.random() * (base / 2);
    this.reconnectAttempt += 1;

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.openSocket();
    }, delay);
  }

  private clearTimers(): void {
    if (this.refreshTimer !== null) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.pingTimer !== null) {
      window.clearTimeout(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
